import { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/stores/RootStore';
import { adminApi } from '@/services/authApi';
import SubPageHero from '@/components/SubPageHero';
import AdminInquiries from './AdminInquiries';
import AdminAs from './AdminAs';
import AccountPanel from './AccountPanel';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { Icon } from './memberIcons';
import m from './Member.module.scss';

/**
 * [2026-09-10 P5 / 2026-09-11 개편] 관리자 — evs.com/solution-partners 밝은 카드형 + ERP 회원 분석.
 *   탭: ①대시보드(KPI·월별가입 그래프·가입경로/기기 분포·방문동향) ②회원관리(검색·페이징·IP·기기·방문·CSV·상세) ③문의관리.
 *   회원정보=영업자산 관점: 마케팅 수신동의 리드, 동면기간, 체류시간까지 노출. 데이터 근거=users/visit_log/inquiries.
 */
interface Row {
	id: number; username: string | null; name: string; phone: string; email: string | null;
	role: string; status: string; protected: number; social_provider: string | null; created_at: string;
	retention_months: number | null; last_login_at: string | null; last_login_ip: string | null;
	last_device: string | null; visit_count: number | null; last_visit_at: string | null; inquiry_count: number | null;
	admin_tags: string | null;
}
interface Note { id: number; author_name?: string; content: string; created_at: string; }
interface UVisit { id: number; path: string; dwell_ms: number; device?: string; created_at: string; }
interface Stats {
	members: Record<string, number>;
	monthly: { ym: string; cnt: number }[];
	visitsMonthly: { ym: string; cnt: number }[];
	visits: Record<string, number>;
	unit: string;   // 'day' | 'month'
	count: number;
}
interface VisitRow { id: number; user_id: number; name?: string; email?: string; path: string; dwell_ms: number; device?: string; ip?: string; created_at: string; }
const STATUS_FILTERS = [
	{ v: '', t: '전체 상태' }, { v: 'ACTIVE', t: '정상' }, { v: 'DORMANT', t: '휴면' },
	{ v: 'SUSPENDED', t: '정지' }, { v: 'PENDING', t: '가입대기' },
];

const socialBadge = (p: string | null) =>
	p === 'google' ? { c: m.bGoogle, t: 'Google' } : p === 'naver' ? { c: m.bNaver, t: '네이버' } : { c: m.bLocal, t: '자체' };
const statusBadge = (s: string) =>
	s === 'SUSPENDED' ? { c: m.bSuspended, t: '정지' } : s === 'PENDING' ? { c: m.bPending, t: '가입미완' }
	: s === 'DORMANT' ? { c: m.bDormant, t: '휴면' } : { c: m.bActive, t: '정상' };
const retLabel = (v: number | null) => (!v ? '탈퇴시까지' : v % 12 === 0 ? `${v / 12}년` : `${v}개월`);
const catLabel = (c: string) => ({ PRODUCT: '제품·솔루션', INSTALL_AS: '설치·상담', QUOTE: '견적·도입', DEMO: '체험·데모', ETC: '기타' } as Record<string, string>)[c] || c;
const ymd = (s: string | null) => (s ? String(s).slice(0, 10) : '-');
function daysSince(s: string | null): number | null {
	if (!s) return null;
	const d = new Date(String(s).replace(' ', 'T'));
	if (isNaN(d.getTime())) return null;
	return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}
function dwellText(sec: number): string {
	if (!sec) return '0초';
	return sec >= 60 ? `${Math.floor(sec / 60)}분 ${sec % 60}초` : `${sec}초`;
}
// 최근 N구간 축(빈 구간 0 채움) — 백엔드는 데이터 있는 구간만 반환. unit=day|month.
function buildSeries(rows: { ym: string; cnt: number }[], unit: string, count: number): { ym: string; cnt: number }[] {
	const map = new Map(rows.map((r) => [r.ym, Number(r.cnt)]));
	const out: { ym: string; cnt: number }[] = [];
	const today = new Date();
	for (let i = count - 1; i >= 0; i--) {
		let key: string;
		if (unit === 'day') {
			const dd = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
			key = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
		} else {
			const dd = new Date(today.getFullYear(), today.getMonth() - i, 1);
			key = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}`;
		}
		out.push({ ym: key, cnt: map.get(key) || 0 });
	}
	return out;
}
// 축 라벨: 일별=DD, 월별=MM월
const axisLabel = (ym: string, unit: string) => (unit === 'day' ? `${ym.slice(8, 10)}` : `${ym.slice(5, 7)}월`);
const PERIODS = [{ v: '30d', t: '30일' }, { v: '6m', t: '6개월' }, { v: '12m', t: '12개월' }, { v: '24m', t: '24개월' }];

// KPI 카드 — onClick 있으면 클릭 가능(해당 회원 목록으로 드릴다운).
function Kpi({ icon, tone, value, label, sub, onClick }: { icon: keyof typeof Icon; tone?: string; value: number | string; label: string; sub?: string; onClick?: () => void }) {
	const Ico = Icon[icon];
	return (
		<div className={`${m.kpi} ${onClick ? m.kpiClickable : ''}`} onClick={onClick}
			role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
			<div className={m.kpiTop}>
				<div className={`${m.kpiIco} ${tone ? m[tone] : ''}`}><Ico /></div>
				{onClick && <span className={m.kpiGo}><Icon.chevron /></span>}
			</div>
			<div className={m.kpiVal}><b>{value}</b></div>
			<div className={m.kpiLabel}>{label}</div>
			{sub && <div className={m.kpiSub}>{sub}</div>}
		</div>
	);
}

// 가입/방문 SVG 막대 그래프(라이브러리 없음). unit=day|month → 라벨·간격 자동.
function SignupBars({ data, unit = 'month' }: { data: { ym: string; cnt: number }[]; unit?: string }) {
	const W = 720, H = 220, padL = 34, padB = 30, padT = 14, padR = 8;
	const max = Math.max(1, ...data.map((d) => d.cnt));
	const bw = (W - padL - padR) / data.length;
	const chartH = H - padB - padT;
	const ticks = 4;
	// 막대가 많으면(일별 30 등) 라벨 솎기: 최대 ~12개만 표시.
	const step = Math.max(1, Math.ceil(data.length / 12));
	return (
		<div className={m.chartWrap}>
			<svg className={m.barSvg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="월별 신규 가입">
				{Array.from({ length: ticks + 1 }).map((_, i) => {
					const y = padT + (chartH * i) / ticks;
					const val = Math.round((max * (ticks - i)) / ticks);
					return (<g key={i}>
						<line className={m.gridLine} x1={padL} y1={y} x2={W - padR} y2={y} />
						<text className={m.barLabel} x={padL - 6} y={y + 3} textAnchor="end">{val}</text>
					</g>);
				})}
				{data.map((d, i) => {
					const h = (d.cnt / max) * chartH;
					const x = padL + i * bw + bw * 0.2;
					const y = padT + chartH - h;
					return (<g key={d.ym}>
						{d.cnt > 0 && <text className={m.barVal} x={x + bw * 0.3} y={y - 5} textAnchor="middle">{d.cnt}</text>}
						<rect className={m.barRect} x={x} y={y} width={bw * 0.6} height={Math.max(0, h)} rx={3} />
						{(i % step === 0 || i === data.length - 1) &&
							<text className={m.barLabel} x={x + bw * 0.3} y={H - padB + 16} textAnchor="middle">{axisLabel(d.ym, unit)}</text>}
					</g>);
				})}
			</svg>
		</div>
	);
}

// 분포 바
function Dist({ name, icon, value, total, cls }: { name: string; icon?: keyof typeof Icon; value: number; total: number; cls: string }) {
	const pct = total > 0 ? Math.round((value / total) * 100) : 0;
	const Ico = icon ? Icon[icon] : null;
	return (
		<div className={m.distRow}>
			<span className={m.distName}>{Ico && <Ico />}{name}</span>
			<span className={m.distTrack}><span className={`${m.distFill} ${cls}`} style={{ width: `${pct}%` }} /></span>
			<span className={m.distVal}>{value} · {pct}%</span>
		</div>
	);
}

const devLabel = (d?: string) => (d === 'MOBILE' ? '모바일' : d === 'TABLET' ? '태블릿' : d === 'PC' ? 'PC' : '-');
// [2026-09-12] SMS 발송 용도 라벨(phone_verify.purpose)
const smsPurposeLabel = (p: string) => (p === 'SIGNUP' ? '가입' : p === 'FIND_ID' ? '아이디 찾기' : p === 'RESET_PW' ? '비번 재설정' : p);
const dtParse = (s: string) => +new Date(String(s).replace(' ', 'T'));
const dtText = (s: string) => String(s).slice(0, 16).replace('T', ' ');

// 활동 타임라인 — 가입·방문·문의를 시간순(최신) 통합.
function Timeline({ visits, inq, joinedAt }: { visits: UVisit[]; inq: { id: number; category: string; title: string; status: string; created_at: string }[]; joinedAt: string }) {
	const items: { t: string; icon: keyof typeof Icon; label: string; sub: string }[] = [
		{ t: joinedAt, icon: 'userPlus', label: '회원 가입', sub: '' },
		...visits.map((v) => ({ t: v.created_at, icon: 'eye' as keyof typeof Icon, label: `방문 · ${v.path}`, sub: `${devLabel(v.device)} · 체류 ${dwellText(Math.round(Number(v.dwell_ms || 0) / 1000))}` })),
		...inq.map((q) => ({ t: q.created_at, icon: 'doc' as keyof typeof Icon, label: `문의 · ${q.title}`, sub: `${catLabel(q.category)} · ${q.status === 'ANSWERED' ? '답변완료' : '답변대기'}` })),
	].sort((a, b) => dtParse(b.t) - dtParse(a.t)).slice(0, 20);
	if (items.length === 0) return <p className={m.empty} style={{ padding: 16 }}>활동 기록이 없습니다.</p>;
	return (
		<div className={m.timeline}>
			{items.map((it, i) => {
				const Ico = Icon[it.icon];
				return (
					<div className={m.tlItem} key={i}>
						<span className={m.tlIco}><Ico /></span>
						<div className={m.tlBody}>
							<div className={m.tlLabel}>{it.label}</div>
							{it.sub && <div className={m.tlSub}>{it.sub}</div>}
						</div>
						<span className={m.tlTime}>{dtText(it.t)}</span>
					</div>
				);
			})}
		</div>
	);
}

function AdminHome() {
	const auth = useAuth();
	const [tab, setTab] = useState<'info' | 'dash' | 'members' | 'inquiries' | 'visits' | 'sms' | 'as'>('dash');
	const [stats, setStats] = useState<Stats | null>(null);
	const [range, setRange] = useState('12m');   // [2026-09-11] 그래프 기간: 30d(일별)/6m/12m/24m
	const [rows, setRows] = useState<Row[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [keyword, setKeyword] = useState('');
	const [q, setQ] = useState('');
	const [statusFilter, setStatusFilter] = useState('');   // [2026-09-11] 회원 상태 필터
	const [marketingFilter, setMarketingFilter] = useState('');   // '' | 'sms' | 'email' (영업 리드)
	const [providerFilter, setProviderFilter] = useState('');     // '' | 'social' (가입경로)
	const [joinedFilter, setJoinedFilter] = useState('');         // '' | 'month' (이번 달 신규)
	// 방문 로그 뷰
	const [visitRows, setVisitRows] = useState<VisitRow[]>([]);
	const [visitTotal, setVisitTotal] = useState(0);
	const [visitPage, setVisitPage] = useState(0);
	const [loading, setLoading] = useState(false);
	const [msg, setMsg] = useState<string | null>(null);
	const [detail, setDetail] = useState<Row | null>(null);
	const [delTarget, setDelTarget] = useState<Row | null>(null);   // [2026-09-12] 회원삭제 확인 = 네이티브 confirm 대신 모달
	const [detailInq, setDetailInq] = useState<{ id: number; category: string; title: string; status: string; created_at: string }[] | null>(null);
	const [detailNotes, setDetailNotes] = useState<Note[]>([]);
	const [detailVisits, setDetailVisits] = useState<UVisit[]>([]);
	const [noteInput, setNoteInput] = useState('');
	const [tagInput, setTagInput] = useState('');
	// [2026-09-12] SMS(SOLAPI) 발송 현황 뷰 — phone_verify 기반(웹 요청분)
	const [smsData, setSmsData] = useState<{ stats: Record<string, number>; series: { d: string; c: number }[]; unit: string } | null>(null);
	const [smsRange, setSmsRange] = useState('30d');
	const [smsRows, setSmsRows] = useState<{ id: number; phone: string; purpose: string; verified: number; attempt_count: number; ip: string; created_at: string }[]>([]);
	const [smsTotal, setSmsTotal] = useState(0);
	const [smsPage, setSmsPage] = useState(0);
	const [smsPurpose, setSmsPurpose] = useState('');
	const [smsVerified, setSmsVerified] = useState('');   // '' 전체 | '0' 미인증 | '1' 완료 (KPI 드릴다운)
	const smsListRef = useRef<HTMLDivElement>(null);       // KPI 클릭 시 발송 내역으로 스크롤
	const size = 20;

	const loadStats = useCallback(async () => {
		const r = await adminApi.stats(range);
		if (r.success) setStats({ members: r.members as Record<string, number>, monthly: (r.monthly as { ym: string; cnt: number }[]) || [], visitsMonthly: (r.visitsMonthly as { ym: string; cnt: number }[]) || [], visits: r.visits as Record<string, number>, unit: (r.unit as string) || 'month', count: Number(r.count) || 12 });
	}, [range]);
	const loadMembers = useCallback(async () => {
		setLoading(true);
		const r = await adminApi.listUsers(q, page, size, statusFilter, marketingFilter, providerFilter, joinedFilter);
		setLoading(false);
		if (r.success) { setRows((r.users as Row[]) || []); setTotal(Number(r.total) || 0); }
		else setMsg(r.message || '목록을 불러오지 못했습니다.');
	}, [q, page, statusFilter, marketingFilter, providerFilter, joinedFilter]);
	const loadVisits = useCallback(async () => {
		const r = await adminApi.visits(visitPage, 30);
		if (r.success) { setVisitRows((r.items as VisitRow[]) || []); setVisitTotal(Number(r.total) || 0); }
	}, [visitPage]);
	const loadSms = useCallback(async () => {
		const r = await adminApi.smsStats(smsRange);
		if (r.success) setSmsData({ stats: r.stats as Record<string, number>, series: (r.series as { d: string; c: number }[]) || [], unit: (r.unit as string) || 'day' });
		const l = await adminApi.smsList(smsPage, 20, smsPurpose, smsVerified);
		if (l.success) { setSmsRows((l.items as typeof smsRows) || []); setSmsTotal(Number(l.total) || 0); }
	}, [smsRange, smsPage, smsPurpose, smsVerified]);

	useEffect(() => { if (auth.isAdmin) loadStats(); }, [loadStats, auth.isAdmin]);
	useEffect(() => { if (auth.isAdmin && tab === 'members') loadMembers(); }, [loadMembers, auth.isAdmin, tab]);
	useEffect(() => { if (auth.isAdmin && tab === 'visits') loadVisits(); }, [loadVisits, auth.isAdmin, tab]);
	useEffect(() => { if (auth.isAdmin && tab === 'sms') loadSms(); }, [loadSms, auth.isAdmin, tab]);

	if (auth.ready && !auth.isAuthed) return <Navigate to="/login" replace />;
	if (auth.ready && !auth.isAdmin) return <Navigate to="/" replace />;

	async function toggleStatus(u: Row) {
		const next = u.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
		const r = await adminApi.setStatus(u.id, next);
		setMsg(r.message || null);
		if (r.success) loadMembers();
	}
	async function doRemove() {
		if (!delTarget) return;
		const r = await adminApi.deleteUser(delTarget.id);
		setDelTarget(null);
		setMsg(r.message || null);
		if (r.success) { loadMembers(); loadStats(); }
	}
	async function openDetail(u: Row) {
		setDetail(u); setDetailInq(null); setDetailNotes([]); setDetailVisits([]); setNoteInput(''); setTagInput(u.admin_tags || '');
		const [inq, nts, vis] = await Promise.all([adminApi.userInquiries(u.id), adminApi.listNotes(u.id), adminApi.userVisits(u.id)]);
		setDetailInq((inq.items as typeof detailInq) || []);
		if (nts.success) setDetailNotes((nts.items as Note[]) || []);
		if (vis.success) setDetailVisits((vis.items as UVisit[]) || []);
	}
	async function addNote() {
		if (!detail || !noteInput.trim()) return;
		const r = await adminApi.addNote(detail.id, noteInput.trim());
		if (r.success) { setNoteInput(''); const n = await adminApi.listNotes(detail.id); if (n.success) setDetailNotes((n.items as Note[]) || []); }
	}
	async function delNote(noteId: number) {
		const r = await adminApi.deleteNote(noteId);
		if (r.success && detail) { const n = await adminApi.listNotes(detail.id); if (n.success) setDetailNotes((n.items as Note[]) || []); }
	}
	async function saveTags() {
		if (!detail) return;
		await adminApi.setTags(detail.id, tagInput.trim());
		setMsg('태그가 저장되었습니다.');
	}
	async function download() {
		const err = await adminApi.exportUsers();
		if (err) setMsg(err);
	}
	function search(e: React.FormEvent) { e.preventDefault(); setPage(0); setQ(keyword.trim()); }
	// [2026-09-11] KPI 클릭 → 회원관리 탭으로 이동 + 필터 드릴다운(한 번에 하나만 적용, 나머지 해제).
	function clearFilters() { setStatusFilter(''); setMarketingFilter(''); setProviderFilter(''); setJoinedFilter(''); setKeyword(''); setQ(''); setPage(0); }
	function goMembers(st = '') { clearFilters(); setStatusFilter(st); setTab('members'); }
	function goMarketing(kind: string) { clearFilters(); setMarketingFilter(kind); setTab('members'); }
	function goProvider(p: string) { clearFilters(); setProviderFilter(p); setTab('members'); }
	function goJoined(j: string) { clearFilters(); setJoinedFilter(j); setTab('members'); }
	// [2026-09-12] SMS KPI 드릴다운 — 발송 내역을 용도/인증상태로 필터하고 목록으로 스크롤.
	function goSms(purpose: string, verified: string) {
		setSmsPurpose(purpose); setSmsVerified(verified); setSmsPage(0);
		setTimeout(() => smsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
	}

	const totalPages = Math.max(1, Math.ceil(total / size));
	const mem = stats?.members || {};
	const vis = stats?.visits || {};
	const social = (mem.google || 0) + (mem.naver || 0);
	const devTotal = (vis.pc || 0) + (vis.mobile || 0) + (vis.tablet || 0);

	return (
		<>
			<SubPageHero align="left" title="관리자" subtitle="회원·문의 관리 · ERP 분석"
				image="/images/auth/member-hero.jpg" crumbs={[{ label: 'HOME' }, { label: '관리자' }]} />
			<div className={m.page}>
				<div className={m.wrap}>
					<div className={m.tabs}>
						<button className={`${m.tab} ${tab === 'info' ? m.tabOn : ''}`} onClick={() => setTab('info')}>내 정보</button>
						<button className={`${m.tab} ${tab === 'dash' ? m.tabOn : ''}`} onClick={() => setTab('dash')}>대시보드</button>
						<button className={`${m.tab} ${tab === 'members' ? m.tabOn : ''}`} onClick={() => setTab('members')}>회원관리</button>
						<button className={`${m.tab} ${tab === 'visits' ? m.tabOn : ''}`} onClick={() => setTab('visits')}>방문로그</button>
						<button className={`${m.tab} ${tab === 'inquiries' ? m.tabOn : ''}`} onClick={() => setTab('inquiries')}>문의관리</button>
						<button className={`${m.tab} ${tab === 'as' ? m.tabOn : ''}`} onClick={() => setTab('as')}>A/S 관리</button>
						<button className={`${m.tab} ${tab === 'sms' ? m.tabOn : ''}`} onClick={() => setTab('sms')}>SMS 현황</button>
					</div>

					{/* ── 내 정보(계정) ── [2026-09-13] 관리자 계정 정보를 별도 마이페이지가 아닌 첫 탭으로 통합(UX 혼란 제거). */}
					{tab === 'info' && <AccountPanel />}

					{/* ── 대시보드 ── */}
					{tab === 'dash' && (
						<>
							<p className={m.eyebrow}>MEMBER ANALYTICS</p>
							<h2 className={m.h2}>회원 <b>동향 분석</b></h2>
							<p className={m.lead}>회원 정보는 영업의 핵심 자산입니다. 가입·활성·마케팅 리드·방문 동향을 한눈에.</p>

							<div className={m.kpiGrid}>
								<Kpi icon="users" tone="" value={mem.total ?? 0} label="총 회원" sub={`관리자 ${mem.admin ?? 0}명 포함`} onClick={() => goMembers('')} />
								<Kpi icon="userPlus" tone="green" value={mem.newThisMonth ?? 0} label="이번 달 신규" sub="이번 달 가입" onClick={() => goJoined('month')} />
								<Kpi icon="check" tone="green" value={mem.active ?? 0} label="활성 회원" onClick={() => goMembers('ACTIVE')} />
								<Kpi icon="share" tone="" value={social} label="소셜 가입" sub={`Google ${mem.google ?? 0} · 네이버 ${mem.naver ?? 0}`} onClick={() => goProvider('social')} />
								<Kpi icon="sms" tone="amber" value={mem.smsLeads ?? 0} label="SMS 수신동의" sub="영업 마케팅 대상" onClick={() => goMarketing('sms')} />
								<Kpi icon="mail" tone="" value={mem.emailLeads ?? 0} label="이메일 수신동의" sub="영업 마케팅 대상" onClick={() => goMarketing('email')} />
								<Kpi icon="userClock" tone="amber" value={mem.pending ?? 0} label="소셜 가입대기" sub="휴대폰 인증 전" onClick={() => goMembers('PENDING')} />
								<Kpi icon="alert" tone="amber" value={mem.atRisk ?? 0} label="휴면 임박" sub="30일 내 만료" onClick={() => goJoined('atrisk')} />
								<Kpi icon="moon" tone="slate" value={mem.dormant ?? 0} label="휴면 회원" onClick={() => goMembers('DORMANT')} />
								<Kpi icon="eye" tone="" value={vis.total ?? 0} label="총 방문(회원)" sub={`이번 달 ${vis.thisMonth ?? 0}`} onClick={() => setTab('visits')} />
								<Kpi icon="clock" tone="" value={dwellText(vis.avgDwellSec ?? 0)} label="평균 체류시간" onClick={() => setTab('visits')} />
							</div>

							<div className={m.card}>
								<div className={m.chartHead}>
									<div className={m.cardTitle} style={{ margin: 0 }}><Icon.chart />{stats?.unit === 'day' ? '일별' : '월별'} 신규 가입 추이</div>
									<div className={m.periodSel}>
										{PERIODS.map((p) => (
											<button key={p.v} className={`${m.periodBtn} ${range === p.v ? m.periodOn : ''}`}
												onClick={() => setRange(p.v)}>{p.t}</button>
										))}
									</div>
								</div>
								<SignupBars data={buildSeries(stats?.monthly || [], stats?.unit || 'month', stats?.count || 12)} unit={stats?.unit || 'month'} />
							</div>

							<div className={m.card} style={{ marginTop: 18 }}>
								<div className={m.cardTitle}><Icon.eye />{stats?.unit === 'day' ? '일별' : '월별'} 방문 추이 (로그인 회원)</div>
								<SignupBars data={buildSeries(stats?.visitsMonthly || [], stats?.unit || 'month', stats?.count || 12)} unit={stats?.unit || 'month'} />
							</div>

							<div className={m.distGrid}>
								<div className={m.card}>
									<div className={m.cardTitle}><Icon.share />가입 경로 분포</div>
									<Dist name="자체가입" icon="userPlus" value={mem.local ?? 0} total={mem.total ?? 0} cls={m.p} />
									<Dist name="Google" icon="share" value={mem.google ?? 0} total={mem.total ?? 0} cls={m.g} />
									<Dist name="네이버" icon="share" value={mem.naver ?? 0} total={mem.total ?? 0} cls={m.n} />
								</div>
								<div className={m.card}>
									<div className={m.cardTitle}><Icon.monitor />접속 기기 분포 (방문 기준)</div>
									<Dist name="PC" icon="monitor" value={vis.pc ?? 0} total={devTotal} cls={m.p} />
									<Dist name="모바일" icon="phone" value={vis.mobile ?? 0} total={devTotal} cls={m.m} />
									<Dist name="태블릿" icon="monitor" value={vis.tablet ?? 0} total={devTotal} cls={m.t} />
								</div>
							</div>
						</>
					)}

					{/* ── 회원관리 ── */}
					{tab === 'members' && (
						<>
							<p className={m.eyebrow}>MEMBERS</p>
							<h2 className={m.h2}>회원 <b>관리</b></h2>
							<p className={m.lead}>가입경로·상태·유효기간·접속 IP/기기·방문·문의까지. 행을 클릭하면 상세와 문의 이력을 봅니다.</p>
							{msg && <div className={`${m.msg} ${m.msgOk}`}>{msg}</div>}

							<form className={m.toolbar} onSubmit={search}>
								<select className={m.input} style={{ flex: '0 0 auto', minWidth: 130 }} value={statusFilter}
									onChange={(e) => { setStatusFilter(e.target.value); setMarketingFilter(''); setProviderFilter(''); setJoinedFilter(''); setPage(0); }}>
									{STATUS_FILTERS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
								</select>
								<input className={m.input} value={keyword} onChange={(e) => setKeyword(e.target.value)}
									placeholder="아이디·이름·전화·이메일 검색" />
								<button className={m.btn} type="submit"><Icon.search />검색</button>
								<button className={`${m.btn} ${m.btnGhost}`} type="button" onClick={download}><Icon.download />CSV 명부</button>
							</form>
							{(marketingFilter || providerFilter || joinedFilter) && (
								<div className={m.filterChip}>
									<span>{marketingFilter ? `영업 리드 · ${marketingFilter === 'sms' ? 'SMS' : '이메일'} 수신동의`
										: providerFilter === 'social' ? '가입경로 · 소셜(Google·네이버)'
										: joinedFilter === 'atrisk' ? '휴면 임박(30일 내 만료)'
										: joinedFilter === 'month' ? '이번 달 신규 가입' : ''}</span>
									<button onClick={() => { setMarketingFilter(''); setProviderFilter(''); setJoinedFilter(''); setPage(0); }} aria-label="필터 해제"><Icon.x /></button>
								</div>
							)}

							<div className={m.tableWrap}>
								<table className={m.table}>
									<thead>
										<tr>
											<th>ID</th><th>아이디/이메일</th><th>이름</th><th>전화</th><th>가입경로</th>
											<th>구분</th><th>상태</th><th>유효기간</th><th>최근 IP</th><th>기기</th>
											<th>방문</th><th>문의</th><th>동면</th><th>가입일</th><th>관리</th>
										</tr>
									</thead>
									<tbody>
										{rows.length === 0 ? (
											<tr><td colSpan={15} className={m.empty}>{loading ? '불러오는 중…' : '회원이 없습니다.'}</td></tr>
										) : rows.map((u) => {
											const sb = socialBadge(u.social_provider), st = statusBadge(u.status);
											const dorm = daysSince(u.last_login_at);
											return (
												<tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(u)}>
													<td>{u.id}</td>
													<td>{u.username || u.email || `${u.social_provider || ''} 소셜`}</td>
													<td>{u.name}</td>
													<td className={m.mono}>{u.phone || '-'}</td>
													<td><span className={`${m.badge} ${sb.c}`}>{sb.t}</span></td>
													<td><span className={`${m.badge} ${u.role === 'ADMIN' ? m.bAdmin : m.bUser}`}>{u.role === 'ADMIN' ? '관리자' : '일반'}</span></td>
													<td><span className={`${m.badge} ${st.c}`}>{st.t}</span></td>
													<td>{retLabel(u.retention_months)}</td>
													<td className={m.mono}>{u.last_login_ip || '-'}</td>
													<td><span className={u.last_device === 'MOBILE' ? m.devMobile : m.devPc}>{u.last_device === 'MOBILE' ? '모바일' : u.last_device === 'TABLET' ? '태블릿' : u.last_device === 'PC' ? 'PC' : '-'}</span></td>
													<td className={m.mono}>{u.visit_count ?? 0}</td>
													<td className={m.mono}>{u.inquiry_count ?? 0}</td>
													<td className={m.mono}>{dorm === null ? '-' : `${dorm}일`}</td>
													<td className={m.mono}>{ymd(u.created_at)}</td>
													<td onClick={(e) => e.stopPropagation()}>
														{u.protected ? <span className={`${m.badge} ${m.bProtected}`}>보호</span> : (
															<div className={m.rowActions}>
																<button className={`${m.miniBtn} ${m.warn}`} onClick={() => toggleStatus(u)}>{u.status === 'SUSPENDED' ? '해제' : '정지'}</button>
																<button className={`${m.miniBtn} ${m.del}`} onClick={() => setDelTarget(u)}>삭제</button>
															</div>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>

							<div className={m.pager}>
								<button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>이전</button>
								<span>{page + 1} / {totalPages} · 총 {total}명</span>
								<button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>다음</button>
							</div>
						</>
					)}

					{tab === 'inquiries' && <AdminInquiries />}
					{tab === 'as' && <AdminAs />}

					{/* ── SMS 현황(SOLAPI, phone_verify 기반) ── */}
					{tab === 'sms' && (
						<>
							<p className={m.eyebrow}>SMS · SOLAPI</p>
							<h2 className={m.h2}>SMS <b>발송 현황</b></h2>
							<p className={m.lead}>휴대폰 인증(가입·아이디찾기·비번재설정) SMS 발송 요청 현황입니다.</p>
							<div style={{ background: '#eef7fd', border: '1px solid #cfeafb', borderRadius: 10, padding: '12px 15px', margin: '0 0 20px', fontSize: 13, color: '#3a5566', lineHeight: 1.65 }}>
								이 통계는 <b>웹에서 발송 요청한 건수</b> 기준입니다. SOLAPI 접수·도달 성공 여부와 정확한 청구액은 SaaS와 계정 공유라 <b>SOLAPI 콘솔</b>에서만 확인됩니다. 요금은 SMS 단문 추정 단가(약 {smsData?.stats.unitKrw ?? 9}원/건)로 계산한 <b>추정치</b>입니다 · 발신번호 02-2039-3488.
							</div>
							<div className={m.kpiGrid}>
								<Kpi icon="sms" tone="" value={smsData?.stats.total ?? 0} label="총 발송(웹)" sub={`오늘 ${smsData?.stats.today ?? 0}건`} onClick={() => goSms('', '')} />
								<Kpi icon="sms" tone="" value={smsData?.stats.month ?? 0} label="이번 달 발송" />
								<Kpi icon="check" tone="green" value={`${smsData?.stats.verifyRate ?? 0}%`} label="인증 완료율" sub={`${smsData?.stats.verified ?? 0}건 완료 · 미인증 보기`} onClick={() => goSms('', '0')} />
								<Kpi icon="userPlus" tone="" value={smsData?.stats.signup ?? 0} label="가입 인증" onClick={() => goSms('SIGNUP', '')} />
								<Kpi icon="search" tone="" value={smsData?.stats.findId ?? 0} label="아이디 찾기" onClick={() => goSms('FIND_ID', '')} />
								<Kpi icon="key" tone="" value={smsData?.stats.resetPw ?? 0} label="비번 재설정" onClick={() => goSms('RESET_PW', '')} />
								<Kpi icon="chart" tone="amber" value={`${(smsData?.stats.estCostMonth ?? 0).toLocaleString()}원`} label="이번 달 추정 요금" sub="추정 · 공유계정" />
								<Kpi icon="chart" tone="slate" value={`${(smsData?.stats.estCostTotal ?? 0).toLocaleString()}원`} label="총 추정 요금" sub="추정 · 공유계정" />
							</div>
							<div className={m.card}>
								<div className={m.chartHead}>
									<div className={m.cardTitle} style={{ margin: 0 }}><Icon.chart />{smsData?.unit === 'day' ? '일별' : '월별'} 발송 추이</div>
									<div className={m.periodSel}>
										{PERIODS.map((p) => (
											<button key={p.v} className={`${m.periodBtn} ${smsRange === p.v ? m.periodOn : ''}`} onClick={() => setSmsRange(p.v)}>{p.t}</button>
										))}
									</div>
								</div>
								<SignupBars data={buildSeries((smsData?.series || []).map((x) => ({ ym: x.d, cnt: Number(x.c) })), smsData?.unit || 'day', smsData?.unit === 'day' ? 30 : (smsRange === '24m' ? 24 : smsRange === '12m' ? 12 : 6))} unit={smsData?.unit || 'day'} />
							</div>
							<div className={m.toolbar} style={{ marginTop: 18 }} ref={smsListRef}>
								<select className={m.input} style={{ flex: '0 0 auto', minWidth: 140 }} value={smsPurpose} onChange={(e) => { setSmsPurpose(e.target.value); setSmsPage(0); }}>
									<option value="">전체 용도</option>
									<option value="SIGNUP">가입</option>
									<option value="FIND_ID">아이디 찾기</option>
									<option value="RESET_PW">비번 재설정</option>
								</select>
								<select className={m.input} style={{ flex: '0 0 auto', minWidth: 130 }} value={smsVerified} onChange={(e) => { setSmsVerified(e.target.value); setSmsPage(0); }}>
									<option value="">전체 인증상태</option>
									<option value="1">인증 완료</option>
									<option value="0">미인증</option>
								</select>
								{(smsPurpose || smsVerified) && (
									<button className={`${m.btn} ${m.btnGhost}`} onClick={() => { setSmsPurpose(''); setSmsVerified(''); setSmsPage(0); }}>필터 해제</button>
								)}
							</div>
							<p style={{ fontSize: 12, color: '#8894a0', margin: '10px 2px 8px', lineHeight: 1.6 }}>
								용도 — 가입: 회원가입 인증 · 아이디찾기/비번재설정: 계정 찾기 인증 &nbsp;|&nbsp; 인증 — 완료: 코드까지 검증됨 · 미인증: 발송했으나 검증 안 됨(이탈·오입력)
							</p>
							<div className={m.tableWrap}>
								<table className={m.table} style={{ minWidth: 680 }}>
									<thead><tr><th>ID</th><th>전화번호</th><th>용도</th><th>인증</th><th>시도</th><th>IP</th><th>발송 시각</th></tr></thead>
									<tbody>
										{smsRows.length === 0 ? <tr><td colSpan={7} className={m.empty}>발송 내역이 없습니다.</td></tr>
											: smsRows.map((r) => (
												<tr key={r.id}>
													<td className={m.mono}>{r.id}</td>
													<td className={m.mono}>{r.phone}</td>
													<td><span className={`${m.badge} ${m.bLocal}`}>{smsPurposeLabel(r.purpose)}</span></td>
													<td>{r.verified ? <span className={`${m.badge} ${m.bActive}`}>완료</span> : <span className={`${m.badge} ${m.bPending}`}>미인증</span>}</td>
													<td className={m.mono}>{r.attempt_count}</td>
													<td className={m.mono}>{r.ip || '-'}</td>
													<td className={m.mono}>{String(r.created_at).slice(0, 16).replace('T', ' ')}</td>
												</tr>
											))}
									</tbody>
								</table>
							</div>
							<div className={m.pager}>
								<button disabled={smsPage <= 0} onClick={() => setSmsPage((p) => p - 1)}>이전</button>
								<span>{smsPage + 1} / {Math.max(1, Math.ceil(smsTotal / 20))} · 총 {smsTotal}건</span>
								<button disabled={(smsPage + 1) * 20 >= smsTotal} onClick={() => setSmsPage((p) => p + 1)}>다음</button>
							</div>
						</>
					)}

					{/* ── 방문 로그 ── */}
					{tab === 'visits' && (
						<>
							<p className={m.eyebrow}>VISIT LOG</p>
							<h2 className={m.h2}>방문 <b>로그</b></h2>
							<p className={m.lead}>로그인 회원의 페이지 방문 기록(최근순). 체류시간·접속 기기·IP를 확인합니다.</p>
							<div className={m.tableWrap}>
								<table className={m.table} style={{ minWidth: 860 }}>
									<thead><tr><th>시각</th><th>회원</th><th>방문 경로</th><th>기기</th><th>체류</th><th>IP</th></tr></thead>
									<tbody>
										{visitRows.length === 0 ? <tr><td colSpan={6} className={m.empty}>방문 기록이 없습니다.</td></tr>
											: visitRows.map((v) => (
												<tr key={v.id}>
													<td className={m.mono}>{String(v.created_at).slice(0, 16).replace('T', ' ')}</td>
													<td>{v.name || '-'}</td>
													<td className={m.mono}>{v.path}</td>
													<td><span className={v.device === 'MOBILE' ? m.devMobile : m.devPc}>{v.device === 'MOBILE' ? '모바일' : v.device === 'TABLET' ? '태블릿' : v.device === 'PC' ? 'PC' : '-'}</span></td>
													<td className={m.mono}>{dwellText(Math.round(Number(v.dwell_ms || 0) / 1000))}</td>
													<td className={m.mono}>{v.ip || '-'}</td>
												</tr>
											))}
									</tbody>
								</table>
							</div>
							<div className={m.pager}>
								<button disabled={visitPage <= 0} onClick={() => setVisitPage((p) => p - 1)}>이전</button>
								<span>{visitPage + 1} / {Math.max(1, Math.ceil(visitTotal / 30))} · 총 {visitTotal}건</span>
								<button disabled={visitPage + 1 >= Math.ceil(visitTotal / 30)} onClick={() => setVisitPage((p) => p + 1)}>다음</button>
							</div>
						</>
					)}
				</div>
			</div>

			{/* 회원 상세 모달 — 정보 + 문의 이력 */}
			<ConfirmModal open={delTarget != null} danger title="회원 삭제" confirmText="삭제"
				message={delTarget ? `'${delTarget.name}' 회원을 삭제하시겠습니까? 되돌릴 수 없습니다.` : ''}
				onConfirm={doRemove} onCancel={() => setDelTarget(null)} />
			{detail && (
				<div className={m.modalBack} data-lenis-prevent onClick={() => setDetail(null)}>
					<div className={m.modal} onClick={(e) => e.stopPropagation()}>
						{/* 헤더 배너 — 아바타 + 이름 + 배지(옅은 시안 그라데이션) */}
						<div className={m.modalHead}>
							<div className={m.mProfile}>
								<div className={m.mAvatar}>{detail.name.charAt(0)}</div>
								<div>
									<div className={m.mName}>{detail.name}</div>
									<div className={m.mBadges}>
										<span className={`${m.badge} ${socialBadge(detail.social_provider).c}`}>{socialBadge(detail.social_provider).t}</span>
										<span className={`${m.badge} ${detail.role === 'ADMIN' ? m.bAdmin : m.bUser}`}>{detail.role === 'ADMIN' ? '관리자' : '일반'}</span>
										<span className={`${m.badge} ${statusBadge(detail.status).c}`}>{statusBadge(detail.status).t}</span>
									</div>
								</div>
							</div>
							<button className={m.mClose} onClick={() => setDetail(null)} aria-label="닫기"><Icon.x /></button>
						</div>
						<div className={m.modalBody}>
							{/* 활동 요약 타일 */}
							<div className={m.statTiles}>
								<div className={m.statTile}><b>{detail.visit_count ?? 0}</b><span>방문</span></div>
								<div className={m.statTile}><b>{detail.inquiry_count ?? 0}</b><span>문의·A/S</span></div>
								<div className={m.statTile}><b>{daysSince(detail.last_login_at) === null ? '-' : daysSince(detail.last_login_at)}</b><span>동면(일)</span></div>
							</div>

							{/* 정보 2열 그리드 */}
							<div className={m.infoGrid}>
								<div className={m.infoCell}><span className={m.iKey}>아이디/이메일</span><span className={m.iVal}>{detail.username || detail.email || '-'}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>이메일</span><span className={m.iVal}>{detail.email || '-'}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>전화</span><span className={m.iVal}>{detail.phone || '-'}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>개인정보 유효기간</span><span className={m.iVal}>{retLabel(detail.retention_months)}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>최근 접속 IP</span><span className={m.iVal}>{detail.last_login_ip || '-'}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>접속 기기</span><span className={m.iVal}>{detail.last_device === 'MOBILE' ? '모바일' : detail.last_device === 'TABLET' ? '태블릿' : detail.last_device === 'PC' ? 'PC' : '-'}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>최근 방문</span><span className={m.iVal}>{ymd(detail.last_visit_at)}</span></div>
								<div className={m.infoCell}><span className={m.iKey}>가입일</span><span className={m.iVal}>{ymd(detail.created_at)}</span></div>
							</div>

							{/* 태그 */}
							<div className={m.cardTitle} style={{ marginTop: 26 }}><Icon.share />태그</div>
							<div className={m.inlineRow}>
								<input className={m.input} value={tagInput} onChange={(e) => setTagInput(e.target.value)}
									placeholder="쉼표로 구분 (예: VIP, 견적진행, 재방문)" style={{ minWidth: 0 }} />
								<button className={m.btn} onClick={saveTags}>저장</button>
							</div>
							{tagInput.trim() && (
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
									{tagInput.split(',').map((t) => t.trim()).filter(Boolean).map((t, i) => (
										<span key={i} className={`${m.badge} ${m.bAdmin}`}>{t}</span>
									))}
								</div>
							)}

							{/* 활동 타임라인 */}
							<div className={m.cardTitle} style={{ marginTop: 28 }}><Icon.activity />활동 타임라인</div>
							{detailInq === null ? <p className={m.hint}>불러오는 중…</p>
								: <Timeline visits={detailVisits} inq={detailInq} joinedAt={detail.created_at} />}

							{/* 관리 메모(CRM) */}
							<div className={m.cardTitle} style={{ marginTop: 28 }}><Icon.doc />관리 메모 ({detailNotes.length})</div>
							<div className={m.inlineRow}>
								<input className={m.input} value={noteInput} onChange={(e) => setNoteInput(e.target.value)}
									onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} placeholder="영업 메모 입력 후 Enter/추가" style={{ minWidth: 0 }} />
								<button className={m.btn} onClick={addNote} disabled={!noteInput.trim()}>추가</button>
							</div>
							{detailNotes.length > 0 && (
								<div className={m.noteList}>
									{detailNotes.map((n) => (
										<div className={m.noteItem} key={n.id}>
											<div className={m.noteBody}>
												<div className={m.noteContent}>{n.content}</div>
												<div className={m.noteMeta}>{n.author_name || '관리자'} · {dtText(n.created_at)}</div>
											</div>
											<button className={m.noteDel} onClick={() => delNote(n.id)} aria-label="삭제"><Icon.x /></button>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
}

export default observer(AdminHome);
